/**
 * Confirmation before unlinking a patient from EHR sync. Unlinking keeps the
 * link row with status Unlinked, so the dialog says so.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  title: {
    id: 'ehrSync.unlink.title',
    defaultMessage: 'Unlink {name}?',
  },
  body: {
    id: 'ehrSync.unlink.body',
    defaultMessage:
      'Sync from {vendor} stops for this patient. Records already in Engage stay where they are.',
  },
  audit: {
    id: 'ehrSync.unlink.audit',
    defaultMessage:
      'The link is kept with status Unlinked so the history stays auditable. Enrolling the patient again reuses this link and runs matching again.',
  },
  theEhr: {
    id: 'ehrSync.unlink.theEhr',
    defaultMessage: 'the EHR',
  },
  cancel: { id: 'ehrSync.unlink.cancel', defaultMessage: 'Cancel' },
  confirm: { id: 'ehrSync.unlink.confirm', defaultMessage: 'Unlink' },
});

/** Props for {@link UnlinkPatientDialog}. */
interface UnlinkPatientDialogProps {
  open: boolean;
  /** Display name of the patient being unlinked. */
  patientName: string;
  /** EHR vendor for the body text, e.g. 'Epic'. Omitted means "the EHR". */
  vendor?: string | null;
  /** True while the unlink request runs. */
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Unlink confirmation dialog.
 *
 * @param props - see {@link UnlinkPatientDialogProps}
 */
export default function UnlinkPatientDialog({
  open,
  patientName,
  vendor,
  loading = false,
  onCancel,
  onConfirm,
}: UnlinkPatientDialogProps) {
  const { formatMessage } = useIntl();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>
        {formatMessage(messages.title, { name: patientName })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {formatMessage(messages.body, {
            vendor: vendor ?? formatMessage(messages.theEhr),
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage(messages.audit)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={loading}
        >
          {formatMessage(messages.confirm)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
